import React, { FC, memo, useCallback, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { DetailStepChip } from '../types';
import StepChip from './StepChip';

/**
 * The row of steps above a detail page. Chips sit on one horizontal track,
 * joined by a thin line, and the track fades out at its right edge - the fade
 * is the "there is more" cue, so the row never grows to fill the screen. The
 * current chip is scrolled into view imperatively, never via state.
 */
const Track = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;

  /* Fade at the right edge - stays as long as a chip is hidden behind it. */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 80px;
    pointer-events: none;
    background: linear-gradient(to right, transparent, var(--eposSurface));
  }
`;

const Scroller = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  padding: 3px 80px 3px 0;

  /* In the hero the fade is off, so no room needs reserving for it - the row stays centered. */
  .is-step & {
    padding-right: 0;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Row = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  /* In the hero: centered while the chips fit, and auto margins drop to zero once they overflow, so scrolling still works. */
  .is-step & {
    margin-left: auto;
    margin-right: auto;
  }
`;

/** Short line between two chips, level with the circle's center. */
const Connector = styled.span`
  flex: 0 0 auto;
  width: 24px;
  height: 2px;
  margin: 39px -10px 0;
  border-radius: 1px;
  background: var(--eposBorderColor);

  .is-step & {
    background: rgba(255, 255, 255, 0.5);
  }
`;

interface Props {
  chips: DetailStepChip[];
  /** Index into `chips` of the page being shown, -1 on the details page (nothing highlighted). */
  currentIndex: number;
}

const StepProgress: FC<Props> = memo(({ chips, currentIndex }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const currentRef = useRef<HTMLDivElement | null>(null);
  const setCurrentRef = useCallback((node: HTMLDivElement | null) => {
    currentRef.current = node;
  }, []);

  /** Shows a fade on each side that still hides chips. */
  const updateFades = useCallback(() => {
    const scroller = scrollerRef.current;
    const track = trackRef.current;
    if (!scroller || !track) {
      return;
    }
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    track.classList.toggle('has-more-left', scroller.scrollLeft > 4);
    track.classList.toggle('has-more-right', scroller.scrollLeft < maxScroll - 4);
  }, []);

  /**
   * Keep the active step centered as the shopper moves through the pages.
   * Scrolls the row's own scroller only - `scrollIntoView` would also scroll
   * every ancestor, including the sliding menu track, and yank the page.
   */
  useEffect(() => {
    const scroller = scrollerRef.current;
    const chip = currentRef.current;
    if (scroller && chip) {
      const target = chip.offsetLeft + chip.offsetWidth / 2 - scroller.clientWidth / 2;
      scroller.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }
    updateFades();
  }, [currentIndex, chips.length, updateFades]);

  return (
    <Track ref={trackRef} data-testid="epos-step-progress">
      <Scroller ref={scrollerRef} onScroll={updateFades}>
        <Row>
          {chips.map((chip, index) => (
            <React.Fragment key={chip.id}>
              {index > 0 && <Connector />}
              <StepChip
                chip={chip}
                position={index + 1}
                current={index === currentIndex}
                chipRef={index === currentIndex ? setCurrentRef : undefined}
              />
            </React.Fragment>
          ))}
        </Row>
      </Scroller>
    </Track>
  );
});

StepProgress.displayName = 'StepProgress';

export default StepProgress;
